import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import {
  Campaign,
  CreateCampaignRequest,
  GetAllCampaignsResponse,
  GetCampaignResponse,
  UpdateCampaignImageRequest,
  UpdateCampaignRequest,
} from "@/types/campaign";
import { FileObject } from "@/types/shared/file";

export class CampaignManagementService {
  // For parity with backend integration: expose same list endpoint here.
  static async getAllCampaigns(
    page: number = 1,
    limit: number = 10,
  ): Promise<GetAllCampaignsResponse> {
    const response = await api.get<GetAllCampaignsResponse>(
      `/admin/campaigns?page=${page}&limit=${limit}`,
    );
    return response;
  }
  static async getCampaign(
    campaignId: string,
    listingsPage: number = 1,
    listingsLimit: number = 10,
    listingStatuses?: string[],
  ): Promise<GetCampaignResponse> {
    const params = new URLSearchParams({
      listingsPage: String(listingsPage),
      listingsLimit: String(listingsLimit),
    });
    if (listingStatuses !== undefined) {
      params.set("listingStatuses", listingStatuses.join(","));
    }
    const response = await api.get<GetCampaignResponse>(
      `/admin/campaigns/${campaignId}?${params.toString()}`,
    );
    return response;
  }
  // Create new campaign (lazy upload pattern - image uploaded after campaign creation)
  static async createCampaign(
    campaignData: CreateCampaignRequest,
  ): Promise<Campaign> {
    const { MediaService } = await import("../media/mediaService");

    // Extract image file from campaignData
    const imageFile = campaignData.thumbnailImage;

    // Create campaign WITHOUT image first (lazy upload)
    const campaignWithoutImage = {
      ...campaignData,
      thumbnailImage: undefined, // Remove image from request
    };

    // api.post already returns response.data, so createResponse is CreateCampaignResponse directly
    // CreateCampaignResponse is { success: true, data: CampaignDto }
    // So createResponse.data is CampaignDto
    const createResponse = await api.post<{ success: boolean; data: Campaign }>(
      "/admin/campaigns",
      campaignWithoutImage,
    );
    const createdCampaign = createResponse.data; // This is CampaignDto

    // If image was provided, upload it now (lazy upload)
    // For Campaign, we create both PREVIEW (100kb) and FULL_SIZE (0.7mb) versions
    // This allows listings/carousels to use small preview images while detail views use fullSize
    if (imageFile && createdCampaign._id) {
      try {
        const { compressImageForPreview, compressImageForFullSize } =
          await import("@/utils/imageCompression");

        // Create preview version (100kb max)
        let previewFile: File;
        let fullSizeFile: File;

        try {
          previewFile = await compressImageForPreview(imageFile);

          // Create fullSize version (0.7mb max)
          fullSizeFile = await compressImageForFullSize(imageFile);
        } catch (compressionError) {
          previewFile = imageFile;
          fullSizeFile = imageFile;
        }

        // Step 3: Upload preview version to S3 and create Media record
        const fileExtension = imageFile.name.split(".").pop() || "jpg";
        const previewFileName = `${imageFile.name.replace(/\.[^/.]+$/, "")}_preview.${fileExtension}`;

        let previewMediaResponse: any = null;

        try {
          const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "campaign",
              createdCampaign._id,
              {
                originalName: previewFileName,
                mimeType: previewFile.type,
                sizeBytes: previewFile.size,
              },
              "preview", // subPath with preview suffix
            );

          await MediaService.uploadFileToS3(previewUploadUrl, previewFile);

          previewMediaResponse = await MediaService.createMediaAfterUpload(
            previewS3Key,
            {
              originalName: previewFileName,
              mimeType: previewFile.type,
              sizeBytes: previewFile.size,
            },
            { campaignId: createdCampaign._id, mediaSize: "PREVIEW" },
          );

          await MediaService.updateMediaStatus(
            previewMediaResponse.id,
            "UPLOADED",
          );
        } catch (previewError) {
          // Continue with fullSize upload even if preview fails
        }

        // Step 4: Upload fullSize version to S3 and create Media record
        const fullSizeFileName = `${imageFile.name.replace(/\.[^/.]+$/, "")}_fullsize.${fileExtension}`;

        let fullSizeMediaResponse: any = null;

        try {
          const { uploadUrl: fullSizeUploadUrl, s3Key: fullSizeS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "campaign",
              createdCampaign._id,
              {
                originalName: fullSizeFileName,
                mimeType: fullSizeFile.type,
                sizeBytes: fullSizeFile.size,
              },
              "fullsize", // subPath with fullsize suffix
            );

          await MediaService.uploadFileToS3(fullSizeUploadUrl, fullSizeFile);

          fullSizeMediaResponse = await MediaService.createMediaAfterUpload(
            fullSizeS3Key,
            {
              originalName: fullSizeFileName,
              mimeType: fullSizeFile.type,
              sizeBytes: fullSizeFile.size,
            },
            { campaignId: createdCampaign._id, mediaSize: "FULL_SIZE" },
          );

          await MediaService.updateMediaStatus(
            fullSizeMediaResponse.id,
            "UPLOADED",
          );
        } catch (fullSizeError) {
          // Continue even if fullSize fails - at least we tried
        }

        // Fetch updated campaign to get presigned image URL
        // api.get already returns response.data, so updatedResponse is GetCampaignResponse directly
        // GetCampaignResponse is { campaign: CampaignDto, listings: ..., totalListings: ... }
        // So updatedResponse.campaign is CampaignDto
        const updatedResponse = await api.get<{
          campaign: Campaign;
          listings: any[];
          totalListings: number;
        }>(`/admin/campaigns/${createdCampaign._id}`);
        return updatedResponse.campaign;
      } catch (error) {
        // Return campaign without image if upload fails
        return createdCampaign;
      }
    }

    return createdCampaign;
  }

  static async updateCampaign(
    updateData: UpdateCampaignRequest,
  ): Promise<void> {
    const { _id, ...data } = updateData;
    await api.patch<void>(`/admin/campaigns/${_id}`, data);
  }

  // Delete campaign
  static async deleteCampaign(campaignId: string): Promise<void> {
    // Mock implementation
    await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API delay

    // TODO: Uncomment when backend is ready
    // await api.delete(`/admin/campaigns/${campaignId}`);
  }

  // Get cancel preview for campaign
  static async getCancelPreview(
    campaignId: string,
  ): Promise<{
    activeAuctionsWithBids: Array<{
      listingId: string;
      title: string;
      bidCount: number;
    }>;
  }> {
    const response = await api.get<{
      success: boolean;
      activeAuctionsWithBids: Array<{
        listingId: string;
        title: string;
        bidCount: number;
      }>;
    }>(`/admin/campaigns/${campaignId}/cancel-preview`);
    return {
      activeAuctionsWithBids: response.activeAuctionsWithBids || [],
    };
  }

  // Cancel campaign
  static async cancelCampaign(
    campaignId: string,
  ): Promise<{
    activeAuctionsWithBids?: Array<{
      listingId: string;
      title: string;
      bidCount: number;
    }>;
  }> {
    const response = await api.post<{
      success: boolean;
      data: Campaign;
      activeAuctionsWithBids?: Array<{
        listingId: string;
        title: string;
        bidCount: number;
      }>;
    }>(`/admin/campaigns/${campaignId}/cancel`);
    return {
      activeAuctionsWithBids: response.activeAuctionsWithBids,
    };
  }

  // Activate campaign (reactivate cancelled campaign)
  static async activateCampaign(campaignId: string): Promise<Campaign> {
    const response = await api.post<{ success: boolean; data: Campaign }>(
      `/admin/campaigns/${campaignId}/activate`,
    );
    return response.data;
  }

  // Add listing to campaign
  static async addListingToCampaign(
    campaignId: string,
    listingId: string,
  ): Promise<void> {
    // Mock implementation - just show success
    await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API delay

    // TODO: Uncomment when backend is ready
    // const response = await api.post<ApiResponse<void>>(`/admin/campaigns/${campaignId}/listings`, { listingId });
    // return response.data; // Extract the data property
  }

  // Remove listing from campaign
  static async removeListingFromCampaign(
    campaignId: string,
    listingId: string,
  ): Promise<void> {
    // Mock implementation - just show success
    await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API delay

    // TODO: Uncomment when backend is ready
    // const response = await api.delete<ApiResponse<void>>(`/admin/campaigns/${campaignId}/listings/${listingId}`);
    // return response.data; // Extract the data property
  }

  // Update campaign image (admin functionality) - lazy upload pattern
  // For Campaign, we create both PREVIEW (100kb) and FULL_SIZE (0.7mb) versions
  static async updateCampaignImage(
    campaignId: string,
    request: UpdateCampaignImageRequest,
  ): Promise<FileObject> {
    const { MediaService } = await import("../media/mediaService");

    // Step 1: Delete ALL existing images if they exist (both preview and fullSize)
    // Use the dedicated endpoint to delete all campaign media
    try {
      await api.delete(`/media/campaign/${campaignId}`);
    } catch (error) {
      // Continue with upload even if deletion fails
    }

    // Step 2: Create both preview and fullSize versions
    const { compressImageForPreview, compressImageForFullSize } =
      await import("@/utils/imageCompression");

    let previewFile: File;
    let fullSizeFile: File;

    try {
      previewFile = await compressImageForPreview(request.image);

      fullSizeFile = await compressImageForFullSize(request.image);
    } catch (compressionError) {
      previewFile = request.image;
      fullSizeFile = request.image;
    }

    // Step 3: Upload preview version to S3 and create Media record
    const fileExtension = request.image.name.split(".").pop() || "jpg";
    const previewFileName = `${request.image.name.replace(/\.[^/.]+$/, "")}_preview.${fileExtension}`;

    let previewMediaResponse: any = null;

    try {
      const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
        await MediaService.requestPresignedUploadUrl(
          "campaign",
          campaignId,
          {
            originalName: previewFileName,
            mimeType: previewFile.type,
            sizeBytes: previewFile.size,
          },
          "preview", // subPath with preview suffix
        );

      await MediaService.uploadFileToS3(previewUploadUrl, previewFile);

      previewMediaResponse = await MediaService.createMediaAfterUpload(
        previewS3Key,
        {
          originalName: previewFileName,
          mimeType: previewFile.type,
          sizeBytes: previewFile.size,
        },
        { campaignId, mediaSize: "PREVIEW" },
      );

      await MediaService.updateMediaStatus(previewMediaResponse.id, "UPLOADED");
    } catch (previewError) {
      // Continue with fullSize upload even if preview fails
    }

    // Step 4: Upload fullSize version to S3 and create Media record
    const fullSizeFileName = `${request.image.name.replace(/\.[^/.]+$/, "")}_fullsize.${fileExtension}`;

    let fullSizeMediaResponse: any = null;

    try {
      const { uploadUrl: fullSizeUploadUrl, s3Key: fullSizeS3Key } =
        await MediaService.requestPresignedUploadUrl(
          "campaign",
          campaignId,
          {
            originalName: fullSizeFileName,
            mimeType: fullSizeFile.type,
            sizeBytes: fullSizeFile.size,
          },
          "fullsize", // subPath with fullsize suffix
        );

      await MediaService.uploadFileToS3(fullSizeUploadUrl, fullSizeFile);

      fullSizeMediaResponse = await MediaService.createMediaAfterUpload(
        fullSizeS3Key,
        {
          originalName: fullSizeFileName,
          mimeType: fullSizeFile.type,
          sizeBytes: fullSizeFile.size,
        },
        { campaignId, mediaSize: "FULL_SIZE" },
      );

      await MediaService.updateMediaStatus(
        fullSizeMediaResponse.id,
        "UPLOADED",
      );
    } catch (fullSizeError) {
      // Continue even if fullSize fails - at least we tried
    }

    // Return PREVIEW version's ID (not FULL_SIZE) because:
    // 1. Campaign listings use PREVIEW version for thumbnails
    // 2. We need to set the PREVIEW mediaId in the campaign's thumbnailMedia relation
    // 3. The FULL_SIZE version is still linked via campaignId and can be fetched when needed
    // Note: The URL will be fetched by the frontend using useMedia hook, so we don't need to fetch it here
    return {
      id: previewMediaResponse?.id || fullSizeMediaResponse?.id || "",
      url: "", // URL will be fetched by frontend using useMedia hook
      name: request.image.name,
      type: request.image.type,
      size: request.image.size,
    };
  }

  // Remove campaign image - delete ALL media (PREVIEW + FULL_SIZE) for this campaign
  static async removeCampaignImage(
    campaignId: string,
    mediaId: string,
  ): Promise<void> {
    // Use the bulk delete endpoint to remove ALL media for this campaign
    await api.delete(`/media/campaign/${campaignId}`);
  }
}
